import React, { useRef, useEffect } from "react";
import { useRequiredMsgs } from "../../store/requiredMsgs";
import { useStore } from "../../store/store";
import { dlOptions, irailOptions } from "./defaults";
import { SelectChangeEvent } from "@mui/material";

interface UpdateObj {
  dlLayout?: string;
  dlLayoutSidelite?: string;
  numberOfLites?: number;
  numberOfLitesSidelite?: number;
  diffSideliteSdl?: boolean;
}

const baseURL = "https://res.cloudinary.com/ag-millworks/image/upload/dlPanels";

const cottage = ["2W1H", "3W1H", "3W2H", "4W1H"];

function litesCloudinaryImg(layout: string, dl?: string) {
  if (!layout) return "";

  const label = !layout.includes("Marginal")
    ? layout
    : layout === "3 Lite Marginal"
      ? "3_LITE_MARGINAL_NO_DIM_500_2"
      : layout === "6 Lite Marginal"
        ? "6_LITE_MARGINAL_NO_DIM_500_2"
        : layout === "9 Lite Marginal"
          ? "9_LITE_MARGINAL"
          : "";

  const addLabel = cottage.includes(layout) ? "_COTTAGE" : "";

  const img =
    dl !== "Add Intermediate Rail"
      ? `${baseURL}/${label}${addLabel}.jpg`
      : `${baseURL}/intermediate_rail/${label}.jpg`;

  return img;
}

function useDLSelection(
  panelLayout: string,
  layout: string,
  swingSidelite: boolean
) {
  const editBtn = useStore((state) => state.isEdit);

  const doorType = useStore((state) => state.quote.doorType);

  const addDL = useStore((state) => state.quote.addDL);

  const addToQuote = useStore((state) => state.addToQuote);

  const findRequired = useRequiredMsgs((state) => state.findRequired);
  const removeRequired = useRequiredMsgs((state) => state.removeRequired);

  const isSDL = addDL === "Add SDL";

  const text =
    doorType === "Pivot" && !isSDL
      ? "Intermediate Rail"
      : isSDL
        ? "SDL"
        : "TDL";

  const calculateNumOfLites = (
    dlLayout: string,
    isCottage: boolean,
    isSidelite: boolean | undefined
  ) => {
    const removeletters = dlLayout.includes("Marginal")
      ? dlLayout.slice(0, 1)
      : dlLayout.replace("W", "").replace("H", "").split("");

    var num = isCottage
      ? parseInt(removeletters[0]) + parseInt(removeletters[1])
      : dlLayout.includes("Marginal")
        ? parseInt(dlLayout.slice(0, 1))
        : parseInt(removeletters[0]) * parseInt(removeletters[1]);

    return !isSidelite
      ? { numberOfLites: num }
      : { numberOfLitesSidelite: num };
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;

    let update: UpdateObj = {};

    if (!swingSidelite) {
      update["dlLayout"] = value;
    } else {
      update["dlLayoutSidelite"] = value;
    }

    if (!value) {
      if (!swingSidelite) {
        update["numberOfLites"] = 0;
      } else {
        update["numberOfLitesSidelite"] = 0;
      }

      return addToQuote(update);
    }

    if (!swingSidelite && value === "9 Lite Marginal")
      update["diffSideliteSdl"] = true;

    if (
      findRequired("Divided Lites Configuration") ||
      findRequired("Sidelite Divided Lites Configuration")
    ) {
      value && !swingSidelite && removeRequired("Divided Lites Configuration");
      value &&
        swingSidelite &&
        removeRequired("Sidelite Divided Lites Configuration");
    }

    var isCottage = cottage.includes(value);
    const litesobj = calculateNumOfLites(value, isCottage, swingSidelite);

    update = { ...update, ...litesobj };
    addToQuote(update);
  };

  const handleDOChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;

    if (!value) {
      addToQuote({ daylightOpening: "" });
    } else {
      addToQuote({ daylightOpening: value });

      if (findRequired("Daylight Opening")) removeRequired("Daylight Opening");
    }
  };

  const editRef = useRef(true);

  useEffect(() => {
    if (editBtn) return;
    editRef.current = true;
  }, [editBtn]);

  useEffect(() => {
    if (!swingSidelite) return;
    if (editBtn && editRef.current) {
      editRef.current = false;
    } else {
      addToQuote({ dlLayoutSidelite: "" });
    }
  }, [swingSidelite, panelLayout]);

  const filterSwingSideliteOptions = (panelLite: string) => {
    if (panelLite === "9 Lite Marginal")
      return [
        { value: "3 Lite Marginal", label: "3 Lite Marginal" },
        { value: "6 Lite Marginal", label: "6 Lite Marginal" },
      ];

    if (panelLite === "3W2H" || (panelLite === "2W1H" || panelLite === "3W1H" || panelLite === "4W1H")) {
      return dlOptions.filter((item) => item.value !== panelLite && item.label?.includes("Cottage"));
    }

    const panelNumHigh = panelLite.slice(-2);
    const matchNumHigh = dlOptions.filter((item) => {
      if (item.value.slice(-2) === panelNumHigh && item.value !== panelLite)
        return item;
    });

    return matchNumHigh;
  };

  const isCottage = !layout ? false : cottage.includes(layout);

  const liteImage = litesCloudinaryImg(layout || "", addDL);

  const options = isSDL
    ? [...dlOptions, { value: "9 Lite Marginal", label: "9 Lite Marginal" }]
    : text === "Intermediate Rail"
      ? irailOptions
      : dlOptions;

  return {
    options,
    liteImage,
    isCottage,
    filterSwingSideliteOptions,
    handleDOChange,
    handleChange,
    text,
    isSDL
  };
}

export default useDLSelection;
